/**
 * /Users/mac/Documents/FG5.1/js/components/TopNav.js
 * TopNav Component
 */

import { db, auth } from '../../src/firebase.js';
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

export class TopNav {
    constructor(containerId, options = {}) {
        this.containerId = containerId;
        this.onBagClick = options.onBagClick || null;
        this.onItemClick = options.onItemClick || null;
        this.onLogoClick = options.onLogoClick || null;
        this.element = null;
        this.navElement = null;
        this.activeItemId = 'home';
    }

    /**
     * Initialize the top nav component
     */
    async init() {
        console.log('TopNav initializing...');
        this.element = document.getElementById(this.containerId);
        
        if (!this.element) {
            throw new Error(`TopNav container #${this.containerId} not found`);
        }
        this.currentUser = null; // Store current user state
        
        // Clear any existing content to ensure fresh render
        this.element.innerHTML = '';
        this.element.classList.add('top-bar');

        // Inject styles for tooltips
        this.injectStyles();

        // Ensure structure exists
        this.renderStructure();
        this.navElement = this.element.querySelector('.top-nav');
        this.render();
        this.attachEventListeners();

        // Real-time Auth Listener to fix login button persistence
        onAuthStateChanged(auth, async (user) => {
            await this.updateAuthState(user);
        });
    }

    injectStyles() {
        const styleId = 'topnav-tooltip-styles';
        if (document.getElementById(styleId)) return;

        const css = `
            .top-nav-item {
                position: relative;
                text-decoration: none;
            }

            /* Remove global pseudo-elements (dots/separators) from the link */
            .top-nav-item::before,
            .top-nav-item::after {
                content: none !important;
                display: none !important;
            }
            
            .top-nav-item .tooltip-text {
                position: absolute;
                top: 100%;
                left: 50%;
                right: auto; /* Override global .tooltip-text style from Sidebar */
                transform: translateX(-50%);
                margin-top: 8px;
                background-color: #000;
                color: #fff;
                padding: 6px 10px;
                border-radius: 6px;
                font-size: 13px;
                font-weight: 700;
                white-space: nowrap;
                opacity: 0;
                visibility: hidden;
                transition: opacity 0.15s ease;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                pointer-events: none;
                z-index: 1000;
            }

            .top-nav-item .tooltip-text::before,
            .top-nav-item .tooltip-text::after {
                content: none;
                display: none;
            }

            .top-nav-item.show-tooltip .tooltip-text {
                opacity: 1;
                visibility: visible;
            }

            .top-nav-item.active {
                color: var(--accent-pink) !important;
            }

            #nav-login-btn .nav-icon-svg {
                display: none;
            }

            @media (max-width: 768px) {
                .topnav-container {
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                    width: 100% !important;
                    padding: 0 16px !important;
                }
                /* Flatten structure for equal spacing */
                .topnav-left, .topnav-right, .top-nav, .top-nav-auth {
                    display: contents !important;
                }
                /* Equal sizing for nav icons */
                .top-nav-item, .fg-icon-btn, .fg-shop-bag-btn, #nav-login-btn {
                    width: 24px !important;
                    height: 24px !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    background: none !important;
                }
                .logo-button {
                    width: 32px !important;
                    height: 32px !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    background: none !important;
                    border: none !important;
                }
                /* Keep the blue theme for the login button on mobile */
                #nav-login-btn {
                    background-color: var(--primary-blue) !important;
                    border-radius: 4px !important;
                    color: white !important;
                }
                .top-nav-item svg, .fg-icon-btn svg, #nav-login-btn .nav-icon-svg {
                    display: block !important;
                    height: 24px !important;
                }
                .logo-icon {
                    display: block !important;
                    height: 32px !important;
                }
                .logo-text, .fg-btn-text, .top-nav-item::after {
                    display: none !important;
                }
                .fg-bag-count {
                    top: -10px !important;
                    right: -10px !important;
                }
            }
        `;

        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = css;
        document.head.appendChild(style);
    }

    // Define data inline to avoid import errors
    getNavData() {
        return [
            { id: 'home', label: 'Home', icon: 'home' },
            { id: 'leagues', label: 'Leagues', icon: 'trophy' },
            { id: 'fund', label: 'Fund', icon: 'trending-up' },
            { id: 'games', label: 'Games', icon: 'gamepad' }
        ];
    }

    getIconPath(iconName) {
        const paths = {
            'home': '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline>',
            'trophy': '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>',
            'trending-up': '<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline>',
            'gamepad': '<line x1="6" y1="12" x2="10" y2="12"></line><line x1="8" y1="10" x2="8" y2="14"></line><line x1="15" y1="13" x2="15.01" y2="13"></line><line x1="18" y1="11" x2="18.01" y2="11"></line><rect x="2" y="6" width="20" height="12" rx="2"></rect>',
            'play-circle': '<circle cx="12" cy="12" r="10"></circle><polygon points="10 8 16 12 10 16 10 8"></polygon>',
            'user': '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle>',
            'log-in': '<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path><polyline points="10 17 15 12 10 7"></polyline><line x1="15" y1="12" x2="3" y2="12"></line>',
            'chat': '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.38 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.38 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>',
            'log-out': '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line>'
        };
        return paths[iconName] || '';
    }

    /**
     * Render the basic structure (Logo + Nav container)
     */
    renderStructure() {
        if (!this.element.querySelector('.logo-container')) {
            this.element.innerHTML = `
                <div class="topnav-container" style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                    <div class="topnav-left" style="display: flex; align-items: center; gap: 15px;">
                        <button class="logo-button" id="sidebarToggle" aria-label="Toggle Menu">
                            <div class="logo-container">
                                <img src="/images/logo2.png" alt="footballgirl" class="logo-icon">
                                <span class="logo-text"><span class="fg-brand-football">football</span><span class="fg-brand-girl">girl</span></span>
                            </div>
                        </button>
                        <nav class="top-nav"></nav>
                    </div>
                    <div class="topnav-right" style="display: flex; align-items: center; gap: 15px;">
                        <button class="fg-icon-btn fg-shop-bag-btn" id="fg-nav-bag-btn" aria-label="Shopping Bag">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width: 24px; height: 24px;">
                                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                                <line x1="3" y1="6" x2="21" y2="6"></line>
                                <path d="M16 10a4 4 0 0 1-8 0"></path>
                            </svg>
                            <span class="fg-bag-count" style="display:none;"></span>
                        </button>
                        <div class="top-nav-auth"></div>
                    </div>
                </div>
            `;
            
            // Ensure container uses flex to separate left (nav) and right (auth)
            this.element.style.display = 'flex';
            this.element.style.justifyContent = 'space-between';
            this.element.style.alignItems = 'center';
        }
    }

    /**
     * Render the top navigation items
     */
    render() {
        if (!this.navElement) return;

        const items = this.getNavData();
        this.navElement.innerHTML = items.map(item => this.createNavItem(item)).join('');
        
        // Attach long press listeners to nav items
        this.navElement.querySelectorAll('.top-nav-item').forEach(item => {
            this.attachLongPress(item);
        });
    }

    /**
     * Create a single top nav item
     */
    createNavItem(item) {
        const iconPath = this.getIconPath(item.icon);
        const isActive = this.activeItemId === item.id;
        return `
            <a href="#" class="top-nav-item ${isActive ? 'active' : ''}" aria-label="${item.label}" data-id="${item.id}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    ${iconPath}
                </svg>
                <span class="tooltip-text">${item.label}</span>
            </a>
        `;
    }

    /**
     * Attach event listeners
     */
    attachEventListeners() {
        // Logo click
        const toggleBtn = this.element.querySelector('#sidebarToggle');
        if (toggleBtn) {
            toggleBtn.addEventListener('click', (e) => {
                console.log('TopNav: Logo clicked');
                e.preventDefault();
                e.stopPropagation();
                if (this.onLogoClick) this.onLogoClick();
            });
        }

        // Nav item clicks
        if (this.navElement) {
            this.navElement.addEventListener('click', (e) => {
                const navItem = e.target.closest('.top-nav-item');
                if (navItem) {
                    e.preventDefault();
                this.handleItemClick(navItem.dataset.id); // Pass the ID directly
                }
            });
        }
        
        // Initial Auth Button Binding (Default to Login)
        this.updateAuthState(null);

        // Sync bag count in TopNav
        window.addEventListener('fg:cartupdated', (e) => {
            const countEl = this.element.querySelector('#fg-nav-bag-btn .fg-bag-count');
            if (countEl) {
                countEl.textContent = e.detail.count;
                const isVisible = e.detail.count > 0;
                countEl.style.display = isVisible ? 'flex' : 'none';
                if (isVisible) {
                    countEl.classList.remove('fg-bag-count--bounce'); // Reset animation
                    void countEl.offsetWidth; // Trigger reflow
                    countEl.classList.add('fg-bag-count--bounce');
                }
            }
        });
    }

    /**
     * Handle navigation item click
     */
    handleItemClick(itemId) {
        if (window.app) {
            window.app.handleNavigation(itemId);
        }
    }

    /**
     * Set the active navigation item and update UI
     */
    setActive(itemId) {
        this.activeItemId = itemId;
        if (!this.element) return;
        
        const allItems = this.element.querySelectorAll('.top-nav-item, #nav-login-btn');
        allItems.forEach(item => { // Update all nav items, including auth buttons
            const id = item.dataset.id || (item.id === 'nav-profile-btn' ? 'profile' : (item.id === 'nav-login-btn' ? 'login' : null));
            if (id === itemId) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });
    }

    /**
     * Tooltip long-press logic
     */
    attachLongPress(element) {
        let pressTimer;
        const start = (e) => {
            if (e.type === 'mousedown' && e.button !== 0) return;
            element.classList.remove('show-tooltip');
            pressTimer = window.setTimeout(() => {
                element.classList.add('show-tooltip');
            }, 600);
        };
        const cancel = () => {
            clearTimeout(pressTimer);
            element.classList.remove('show-tooltip');
        };
        element.addEventListener('mousedown', start);
        element.addEventListener('touchstart', start, { passive: true });
        element.addEventListener('mouseup', cancel);
        element.addEventListener('mouseleave', cancel);
        element.addEventListener('touchend', cancel);
        element.addEventListener('touchcancel', cancel);
    }

    /**
     * Update the auth button based on user state
     */
    async updateAuthState(user) {
        this.currentUser = user; // Update internal user state
        const container = this.element.querySelector('.top-nav-auth');
        if (!container) return;

        if (user) {
            // Check if the user is a creator and onboarded
            let isCreator = false;
            let isOnboarded = false;
            try {
                const creatorDocRef = doc(db, "creators", user.uid);
                const creatorSnap = await getDoc(creatorDocRef);
                if (creatorSnap.exists()) {
                    isCreator = true;
                    isOnboarded = creatorSnap.data().onboardingComplete;
                }
            } catch (e) {
                console.error("Error checking creator status in TopNav:", e);
            }

            const profilePage = isCreator && isOnboarded ? 'creator-profile' : 'profile';
            const profileLabel = isCreator ? 'My Creator Profile' : 'My Profile';

            // User is logged in -> Show Profile Icon
            container.innerHTML = `
                <button class="top-nav-item ${this.activeItemId === profilePage ? 'active' : ''}" id="nav-profile-btn" aria-label="${profileLabel}" data-id="${profilePage}">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        ${this.getIconPath('user')}
                    </svg>
                    <span class="tooltip-text">${profileLabel}</span>
                </button>`;

            const btn = container.querySelector('#nav-profile-btn');
            this.attachLongPress(btn);
            btn.addEventListener('click', () => {
                if (window.app) window.app.handleNavigation(profilePage);
            });
        } else {
            // User is logged out -> Show Login
            container.innerHTML = `
                <button class="fg-btn fg-btn--primary ${this.activeItemId === 'login' ? 'active' : ''}" id="nav-login-btn" data-id="login">
                    <svg class="nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        ${this.getIconPath('log-in')}
                    </svg>
                    <span class="fg-btn-text">Login</span>
                </button>`;
            const btn = container.querySelector('#nav-login-btn');
            btn.addEventListener('click', () => {
                if (window.app) window.app.handleNavigation('login');
            });
        }
    }
}